import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Spin, notification, Tabs, DatePicker, Select } from "antd";
import { XIcon, Asterisk, UserProfileSuccess, UserProfileError } from "../../../../assets/icons";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { httpService } from "../../../../base/services/httpService.service";
import dayjs from "dayjs";
import { LocalStorageService } from "../../../../base/services/local-storage.service";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./filters.scss";
import { useTranslation } from "../../../../i18n/LanguageContext";

const { TabPane } = Tabs;

const EditProfileModal = ({ visible, onCancel, onOk, handleUpdateOrganization, organization }) => {
   const { t } = useTranslation();
   const [orgSectorsList, setOrgSectorsList] = useState([]);
   const [orgSizeList, setOrgSizeList] = useState([]);
   const [loading, setLoading] = useState(false);
   const [countryList, setCountryList] = useState([]);
   const [cityList, setCityList] = useState([]);
   const [selectedCountryId, setSelectedCountryId] = useState(null);
   const organizationId = LocalStorageService.getItem("user").organizationId;
   const [formData, setFormData] = useState({
      internationalName: "",
      description: "",
      dateOfEstablishment: null,
      address: "",
      email: "",
      phoneNumber: "",
      website: "",
      facebook: "",
      organizationSectors: [],
      organizationSizeId: 0,
      operatingCountriesIds: [],
      baseCountryId: "",
      cityId: "",
      zipCode: ""
   });

   const customNotificationSucess = (
      <div className="custom-notification-container">
         <div className="custom-notification-image-container">
            <img src={UserProfileSuccess} alt="UserProfileSuccess" />
         </div>
         <div className="custom-notification-text-container">
            <h3>{t("profile-updated-success-title")}</h3>
            <p>{t("profile-updated-success-description")}</p>
         </div>
      </div>
   );
   const customNotificationError = (
      <div className="custom-notification-container-error">
         <div className="custom-notification-image-container">
            <img src={UserProfileError} alt="UserProfileError" />
         </div>
         <div className="custom-notification-text-container-error">
            <h3>{t("toast-oops-title")}</h3>
            <p>{t("profile-update-error-description")}</p>
         </div>
      </div>
   );

   const openNotificationSuccess = () => {
      notification.open({
         description: customNotificationSucess,
         placement: "top"
      });
   };

   const openNotificationError = () => {
      notification.open({
         description: customNotificationError,
         placement: "top"
      });
   };

   const fetchOrganization = () => {
      if (organizationId) {
         setFormData({
            ...formData,
            id: organizationId,
            internationalName: organization?.internationalName,
            description: organization?.description,
            dateOfEstablishment: organization?.dateOfEstablishment,
            address: organization?.address,
            email: organization?.email,
            phoneNumber: organization?.phoneNumber,
            website: organization?.website,
            facebook: organization?.facebook,
            linkedIn: organization?.linkedIn,
            instagram: organization?.instagram,
            twitter: organization?.twitter,
            onGoingProjects: organization?.onGoingProjects,
            organizationSectors: organization?.organizationSectors?.map((sector) => ({
               value: sector?.id,
               label: sector?.name
            })),
            operatingCountriesIds: organization?.countries?.map((country) => ({
               value: country?.id,
               label: country?.name
            })),
            organizationSizeId: organization?.organizationSize?.id,
            baseCountryId: organization?.baseCountry?.id,
            cityId: organization?.city?.id,
            zipCode: organization?.zipCode
         });
         setSelectedCountryId(organization?.baseCountry?.id);
      }
   };

   const getSectors = async () => {
      await httpService.get(`/Organizations/sectors`, (res) => {
         const data = res.data.map((orgSectors) => {
            return {
               value: orgSectors.id,
               label: orgSectors.name
            };
         });
         setOrgSectorsList(data);
      });
   };

   const getOrganizationSizes = async () => {
      await httpService.get(`/Organizations/sizes`, (res) => {
         const data = res.data.map((orgSize) => {
            return {
               value: orgSize.id,
               label: orgSize.range
            };
         });
         setOrgSizeList(data);
      });
   };

   const getCountries = async () => {
      await httpService.get(`/Countries`, (res) => {
         const data = res.data.map((country) => {
            return {
               value: country.id,
               label: country.name
            };
         });
         setCountryList(data);
      });
   };

   useEffect(() => {
      if (visible) {
         getSectors();
         getOrganizationSizes();
         getCountries();
         fetchOrganization();
      }
   }, [visible]);

   useEffect(() => {
      if (selectedCountryId) {
         httpService.get(`/countries/${selectedCountryId}/cities`, (res) => {
            const cityData = res.data.map((city) => ({
               value: city.id,
               label: city.name
            }));
            setCityList(cityData);
         });
      }
   }, [selectedCountryId]);

   const validationSchema = Yup.object().shape({
      internationalName: Yup.string().required(t("international-name-required-validation")),
      dateOfEstablishment: Yup.date()
         .nullable()
         .required(t("date-of-establishment-required-validation")),
      address: Yup.string().required(t("address-required-validation")),
      zipCode: Yup.string().required(t("zip-code-required-validation")),
      email: Yup.string()
         .email(t("invalid-email-validation"))
         .required(t("email-required-validation")),
      phoneNumber: Yup.string()
         .nullable()
         .test("is-valid-phone-number", t("invalid-phone-number-validation"), (value) => {
            if (!value) return false;
            return value.startsWith("+") && value.length > 7;
         })
         .required(t("phone-number-required-validation")),
      organizationSectors: Yup.array().min(1, t("at-least-one-sector-required-validation")),
      organizationSizeId: Yup.number().min(1, t("organization-size-required-validation")),
      operatingCountriesIds: Yup.array().min(1, t("at-least-one-country-required-validation")),
      baseCountryId: Yup.string()
         .nullable()
         .required(t("base-country-required-validation")),
      cityId: Yup.string()
         .nullable()
         .when("baseCountryId", {
            is: (baseCountryId) => !!baseCountryId,
            then: Yup.string().required(t("city-required-validation")),
            otherwise: Yup.string().nullable()
         })
   });
   const [activeTab, setActiveTab] = useState("1");

   const handleSubmit = async (values) => {
      const operatingCountriesIdsValue =
         values?.operatingCountriesIds?.map((country) =>
            typeof country === "object" ? country?.value : country
         ) || [];
      const organizationSectorsIdsValue =
         values?.organizationSectors?.map((sector) =>
            typeof sector === "object" ? sector?.value : sector
         ) || [];
      setLoading(true);
      const serializedValues = {
         ...values,
         operatingCountriesIds: operatingCountriesIdsValue,
         organizationSectors: organizationSectorsIdsValue,
         dateOfEstablishment: dayjs(values?.dateOfEstablishment)?.format("YYYY-MM-DD")
      };
      httpService.put(
         "/Organizations",
         serializedValues,
         (res) => {
            if (res.status === 200) {
               httpService.get(`/Organizations/${organizationId}`, (res) => {
                  handleUpdateOrganization(res.data);
               });
               fetchOrganization();
               onOk();
               openNotificationSuccess();
               setLoading(false);
            }
         },
         (error) => {
            openNotificationError();
            setLoading(false);
         }
      );
   };

   return (
      <Modal
         open={visible}
         onCancel={onCancel}
         footer={null}
         closable={false}
         destroyOnClose
         maskClosable={!loading}
      >
         <Formik
            initialValues={formData}
            validationSchema={validationSchema}
            onSubmit={(values) => {
               handleSubmit(values);
            }}
         >
            {({ errors, values, handleSubmit, handleChange, setFieldValue, dirty }) => (
               <Form>
                  <div>
                     <img
                        src={XIcon}
                        alt="XIcon"
                        onClick={onCancel}
                        style={{ float: "right", cursor: "pointer" }}
                     />
                     <div className="add-representative-details">
                        <h1>{t("edit-profile-title")}</h1>
                     </div>
                     <Tabs
                        defaultActiveKey="1"
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key)}
                     >
                        <TabPane tab={t("organization-details-tab")} key="1">
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("organization-name-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.internationalName}
                                 type="text"
                                 name="internationalName"
                                 onChange={handleChange}
                                 placeholder={t("international-name-placeholder")}
                              />
                           </div>
                           <div className="error-message">{errors.internationalName}</div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <label>{t("organization-description-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.description}
                                 type="text"
                                 name="description"
                                 onChange={handleChange}
                                 placeholder={t("description-placeholder")}
                              />
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("foundation-year-label")}</label>
                              </div>
                              <DatePicker
                                 name="dateOfEstablishment"
                                 value={
                                    values.dateOfEstablishment
                                       ? dayjs(values.dateOfEstablishment)
                                       : null
                                 }
                                 onChange={(date) =>
                                    setFieldValue("dateOfEstablishment", date || null)
                                 }
                                 style={{ width: "100%" }}
                                 disabledDate={(current) => {
                                    return current && current > dayjs().endOf("day");
                                 }}
                              />
                              {errors.dateOfEstablishment && (
                                 <div className="error-message">{errors.dateOfEstablishment}</div>
                              )}
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("organization-size-label")}</label>
                              </div>
                              <Select
                                 showSearch
                                 filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                 }
                                 name="organizationSizeId"
                                 options={orgSizeList}
                                 value={orgSizeList.find(
                                    (option) => option.value === values.organizationSizeId
                                 )}
                                 onChange={(selectedOption) =>
                                    setFieldValue("organizationSizeId", selectedOption)
                                 }
                                 style={{ width: "100%" }}
                                 placeholder={t("select-organization-size-placeholder")}
                              />

                              {errors.organizationSizeId && (
                                 <div className="error-message">{errors.organizationSizeId}</div>
                              )}
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("countries-where-operates-label")}</label>
                              </div>
                              <Select
                                 mode="multiple"
                                 name="operatingCountriesIds"
                                 options={countryList}
                                 value={values.operatingCountriesIds}
                                 onChange={(selectedOptions) => {
                                    setFieldValue("operatingCountriesIds", selectedOptions);
                                 }}
                                 placeholder={t("select-operating-countries-placeholder")}
                                 style={{ width: "100%" }}
                              />
                              {errors.operatingCountriesIds && (
                                 <div className="error-message">{errors.operatingCountriesIds}</div>
                              )}
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("working-sector-label")}</label>
                              </div>
                              <Select
                                 mode="multiple"
                                 name="organizationSectors"
                                 options={orgSectorsList}
                                 value={values.organizationSectors}
                                 onChange={(selectedOptions) => {
                                    setFieldValue("organizationSectors", selectedOptions);
                                 }}
                                 style={{ width: "100%" }}
                                 placeholder={t("select-sectors-alt-placeholder")}
                              />
                              {errors.organizationSectors && (
                                 <div className="error-message">{errors.organizationSectors}</div>
                              )}
                           </div>
                        </TabPane>
                        <TabPane tab={t("contact-information-tab")} key="2">
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("organizations-email-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.email}
                                 type="email"
                                 name="email"
                                 onChange={handleChange}
                                 placeholder={t("email")}
                              />
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("base-country-label")}</label>
                              </div>
                              <Select
                                 name="baseCountryId"
                                 options={countryList}
                                 value={countryList.find(
                                    (option) => option.value === values.baseCountryId
                                 )}
                                 onChange={(selectedOption) => {
                                    setFieldValue("baseCountryId", selectedOption);
                                    setSelectedCountryId(selectedOption);
                                    setFieldValue("cityId", null);
                                 }}
                                 placeholder={t("select-base-country-placeholder")}
                                 style={{ width: "100%" }}
                              />
                           </div>
                           <div className="error-message">{errors.baseCountryId}</div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("city")}</label>
                              </div>
                              <Select
                                 name="cityId"
                                 options={cityList}
                                 value={cityList.find((option) => option.value === values.cityId)}
                                 onChange={(selectedOption) => {
                                    setFieldValue("cityId", selectedOption);
                                 }}
                                 placeholder={t("select-a-city-placeholder")}
                                 style={{ width: "100%" }}
                              />
                           </div>
                           <div className="error-message">{errors.cityId}</div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("address-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.address}
                                 type="text"
                                 name="address"
                                 onChange={handleChange}
                                 placeholder={t("address-label")}
                              />
                           </div>
                           <div className="error-message">{errors.address}</div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("zip-code-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.zipCode}
                                 type="text"
                                 name="zipCode"
                                 onChange={handleChange}
                                 placeholder={t("address-label")}
                              />
                           </div>
                           <div className="error-message">{errors.zipCode}</div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <img src={Asterisk} alt="Asterisk" />
                                 <label>{t("phone-number")}</label>
                              </div>
                              <PhoneInput
                                 international
                                 defaultCountry="US"
                                 value={values.phoneNumber}
                                 onChange={(value) => setFieldValue("phoneNumber", value)}
                                 className="custom-phone-input"
                              />
                              {errors.phoneNumber && (
                                 <div className="error-message">{errors.phoneNumber}</div>
                              )}
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <label>{t("website-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.website}
                                 type="text"
                                 name="website"
                                 onChange={handleChange}
                                 placeholder={t("website-label")}
                              />
                           </div>
                           <div>
                              <div className="label-name-add-representative-details">
                                 <label>{t("facebook-label")}</label>
                              </div>
                              <Input
                                 autoComplete="off"
                                 value={values.facebook}
                                 type="text"
                                 name="facebook"
                                 onChange={handleChange}
                                 placeholder={t("facebook-label")}
                              />
                           </div>
                        </TabPane>
                     </Tabs>
                     <div className="footer-buttons-add-representative-details" key="uniqueKey">
                        <Button
                           className="cencel-delete-button"
                           key="cancel"
                           onClick={onCancel}
                           disabled={loading}
                        >
                           {t("cancel")}
                        </Button>
                        <Button
                           className="delete-button"
                           // htmlType="submit"
                           onClick={() => {
                              if (
                                 errors.internationalName ||
                                 errors.dateOfEstablishment ||
                                 errors.organizationSizeId ||
                                 errors.operatingCountriesIds ||
                                 errors.organizationSectors
                              ) {
                                 setActiveTab("1");
                              } else if (
                                 errors.email ||
                                 errors.baseCountryId ||
                                 errors.cityId ||
                                 errors.address ||
                                 errors.zipCode ||
                                 errors.phoneNumber
                              ) {
                                 setActiveTab("2");
                              } else {
                                 handleSubmit();
                              }
                           }}
                           disabled={!dirty || loading}
                        >
                           {loading ? <Spin /> : t("save-changes-button")}
                        </Button>
                     </div>
                  </div>
               </Form>
            )}
         </Formik>
      </Modal>
   );
};

export default EditProfileModal;
